import type { AssetPricesPayload, AssetRelationType } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { View } from 'react-native'
import Icon from '~/components/Icon'
import { Detail, DisplayValue, Label } from '~/components/typography/Content.tsx'
import { Input } from '~/components/ui/input'
import { PriceInput } from '~/components/ui/PriceInput'
import { cn } from '~/lib/utils.ts'

export type PriceEditorProps = {
  data: AssetPricesPayload
  relationType: AssetRelationType
  onUpdate: (data: AssetPricesPayload) => void
  variant?: 'default' | 'inline'
}

export function PriceEditor({
  data,
  relationType,
  onUpdate,
  variant = 'default',
}: PriceEditorProps) {
  const { t } = useTranslations('property')
  const isProspect = relationType === 'prospect'
  const isReference = relationType === 'reference'

  const parsedDisplayPrice = Number.parseFloat(
    data.displayPrice
      ?.toString()
      .replaceAll(/[^\d,-]/g, '')
      .replace(',', '.') || '0'
  )

  const parsedFinalPrice = Number.parseFloat(
    data.finalPrice
      ?.toString()
      .replaceAll(/[^\d,-]/g, '')
      .replace(',', '.') || '0'
  )

  const difference = parsedFinalPrice - parsedDisplayPrice
  const isPositive = difference > 0
  const differencePercentage =
    parsedDisplayPrice > 0 ? ((difference / parsedDisplayPrice) * 100).toFixed(2) : '0'

  const priceLabel = isProspect
    ? t(($) => $.price.estimatedPriceLabel)
    : t(($) => $.price.askingPriceLabel)
  const inputVariant = variant === 'inline' ? 'line-dark' : 'box'

  return (
    <View className={cn('gap-6', variant === 'inline' && 'gap-4')}>
      <View className="gap-2">
        {variant === 'default' && <Label>{priceLabel}</Label>}
        <PriceInput
          placeholder={
            isProspect
              ? t(($) => $.price.estimatedPricePlaceholder)
              : t(($) => $.price.askingPricePlaceholder)
          }
          value={parsedDisplayPrice}
          onChange={(value) => onUpdate({ ...data, displayPrice: Number(value) })}
          variant={inputVariant}
        />
        {variant === 'default' && isProspect && (
          <Detail>{t(($) => $.price.estimatedSalePrice)}</Detail>
        )}
      </View>

      {isReference && (
        <>
          <View className="gap-2">
            {variant === 'default' && <Label>{t(($) => $.price.finalPrice)}</Label>}
            <Input
              placeholder={t(($) => $.price.finalPricePlaceholder)}
              value={parsedFinalPrice}
              onChangeText={(value) => onUpdate({ ...data, finalPrice: Number(value) })}
              keyboardType="numeric"
              formatted
              type="numeric"
              variant={inputVariant}
              thousandSeparator=" "
              decimalSeparator=","
              decimalScale={0}
              fixedDecimalScale
              showClearButton={variant === 'default'}
              suffix={t(($) => $.price.euroSuffix)}
              allowNegative={false}
              inputClassName={variant === 'inline' ? 'text-center font-light text-xl' : undefined}
            />
            {variant === 'default' && <Detail>{t(($) => $.price.actualSalePrice)}</Detail>}
          </View>

          <View className="gap-2">
            {variant === 'default' && <Label>{t(($) => $.price.priceDifference)}</Label>}
            <View className="flex-row items-center gap-2">
              {difference === 0 ? (
                <Icon name="remove" size={24} className="text-foreground" />
              ) : isPositive ? (
                <Icon name="trending-up" size={24} color="green" />
              ) : (
                <Icon name="trending-down" size={24} color="#525252" />
              )}
              <DisplayValue
                className={cn(
                  'font-medium',
                  difference === 0
                    ? 'text-foreground'
                    : isPositive
                      ? 'text-green-600'
                      : 'text-neutral-600'
                )}>
                {isPositive ? '+' : ''}
                {/* TODO: Add support for different currencies. */}
                {difference.toLocaleString('fi-FI')} € ({differencePercentage}%)
              </DisplayValue>
            </View>
            {variant === 'default' && (
              <Detail>{t(($) => $.price.priceDifferenceDescription)}</Detail>
            )}
          </View>
        </>
      )}
    </View>
  )
}
